import axios from 'axios'
import { API_BASE_URL, API_ENDPOINTS } from '../config/api'
import { store } from '../store/store'
import { logout } from '../store/slices/authSlice'
import { createSecureChannel } from './secureChannel'

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Global API loading tracker — Layout listens to this event and shows the
// common LoaderOverlay while any API call is in flight
let pendingRequests = 0
const emitApiLoading = () => {
  window.dispatchEvent(new CustomEvent('api-loading', { detail: pendingRequests > 0 }))
}
const trackStart = () => {
  pendingRequests += 1
  emitApiLoading()
}
const trackEnd = () => {
  pendingRequests = Math.max(0, pendingRequests - 1)
  emitApiLoading()
}

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // skipLoader: high-frequency background calls (e.g. live username
    // availability) must not flash the global LoaderOverlay
    if (!config.skipLoader) trackStart()
    const activeRole = localStorage.getItem('activeRole')
    let token = null

    if (activeRole === 'owner') {
      token = localStorage.getItem('ownerToken')
    } else if (activeRole === 'staff') {
      token = localStorage.getItem('staffToken')
    } else if (activeRole === 'executive') {
      token = localStorage.getItem('executiveToken')
    } else {
      token = localStorage.getItem('adminToken')
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    if (!error.config?.skipLoader) trackEnd()
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    if (!response.config?.skipLoader) trackEnd()
    if (response.status === 304 && response.data) {
      return response
    }
    return response
  },
  (error) => {
    if (!error.config?.skipLoader) trackEnd()
    if (error.response?.status === 401) {
      console.log('401 Unauthorized - Auto logging out...')
      store.dispatch(logout())

      localStorage.removeItem('adminToken')
      localStorage.removeItem('ownerToken')
      localStorage.removeItem('staffToken')
      localStorage.removeItem('executiveToken')
      localStorage.removeItem('executivePermissions')
      localStorage.removeItem('activeRole')
      localStorage.removeItem('adminUser')

      // '/' and '/login' are the public site, not the admin login — bouncing a
      // 401 off them would be a cross-app redirect, so only the admin login URL
      // (both slash forms) suppresses the redirect.
      if (!['/admin', '/admin/'].includes(window.location.pathname)) {
        window.location.href = '/admin'
      }
    }
    return Promise.reject(error)
  }
)

// Secure channel (server API_ENCRYPTION=on): decrypts every response before any
// caller sees it. Installed after the interceptors above, so they still see the
// raw response and every service receives plain JSON. The admin socket uses the
// same session — see socketClient.js.
export const secureChannel = createSecureChannel({ handshakeUrl: `${API_BASE_URL}/cs/handshake` })
secureChannel.installHttp(api)

export const getStaffProfile = async () => {
  const response = await api.get('/admin/staff/profile')
  return response.data
}

export const getOwnerProfile = async () => {
  const response = await api.get('/admin/owner/profile')
  return response.data
}

export const getSportsTree = async () => {
  const response = await api.get(API_ENDPOINTS.GET_SPORTS_TREE)
  return response.data
}
/* The ordered tree. Falls back to the raw tree endpoint so an admin build can
   ship before the server that serves /events does — the caller can tell which
   it got by the `ordered` flag. */
export const getSportsEvents = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.GET_SPORTS_EVENTS)
    const sports = response.data?.data?.sports
    if (Array.isArray(sports)) return { ordered: true, sports }
  } catch (err) {
    console.warn('sports/events unavailable, falling back to sports/tree:', err?.message)
  }
  const legacy = await getSportsTree()
  return { ordered: false, legacy }
}


export const getSportsData = async (id) => {
  const response = await api.post(API_ENDPOINTS.GET_SPORT_DATA, { id })
  return response.data
}

export const getMatchPrivateData = async (gmid, sid) => {
  const response = await api.post(API_ENDPOINTS.GET_MATCH_PRIVATE_DATA, { gmid, sid })
  return response.data
}

// More Market list: every market the match has shown (server redis store)
// combined with the live feed — [{ mid, mname, sno }]
export const getMarketList = async (gmid, sid) => {
  const response = await api.post(API_ENDPOINTS.GET_MARKET_LIST, { gmid, sid })
  return response.data
}

// Match header/meta — `gtv` (0 = no score animation for this match), `scard`,
// `tv`, plus ename/cname/stime. Polled, so it must not flash the loader.
export const getSportsGameDetail = async (gmid, etid) => {
  const response = await api.get(API_ENDPOINTS.GET_SPORTS_GAME_DETAIL, { params: { gmid, etid }, skipLoader: true })
  const d = response.data
  const arr = d?.data?.data || d?.data || d
  return Array.isArray(arr) ? arr[0] : arr
}

/* The score-animation url. The token inside it is SINGLE USE — call this once
   per match and never re-fetch to "check" it (that check is the one use). */
export const getScoreUrl = async (gmid, etid) => {
  const response = await api.get(API_ENDPOINTS.GET_SCORE_URL, { params: { gmid, etid }, skipLoader: true })
  return response.data?.data?.url || null
}

export const getNetExposure = async () => {
  const response = await api.get(`${API_BASE_URL}/d99/net-exposure/sports`)
  return response.data
}

export const getMatchExposureSummary = async (token, matchId) => {
  try {
    const response = await api.get(`${API_BASE_URL}/d99/net-exposure?matchId=${matchId}`)
    return response.data
  } catch (error) {
    return { success: false, message: error.message }
  }
}

export const getSportsScoreCard = async (gmid, sid) => {
  try {
    const response = await api.post(API_ENDPOINTS.GET_SPORTS_SCORE_CARD, { gmid, sid })
    if (response.data && response.data.success && response.data.data?.score_url) {
      return response.data.data.score_url
    }
    return null
  } catch (error) {
    return null
  }
}

export const searchByUsername = async (username, page = 1, limit = 10) => {
  const response = await api.get(`/admin/search-by-username`, {
    params: { username, page, limit }
  })
  return response.data
}

export const getBetList = async (params) => {
  const response = await api.get(API_ENDPOINTS.REPORTS.CURRENT_BETS, { params })
  return response.data
}

export const getMatchDownlineBets = async (params) => {
  const response = await api.get('/admin/reports/match-downline-bets', { params })
  return response.data
}

export const getMatchedBetsByEvent = async (eventId) => {
  const response = await api.get(`/user/cricket/matched-bets/all?eventid=${eventId}`)
  return response.data
}

// Pass { eventid } (or { match_id }) to scope the response to a single match.
export const getMarketAnalysis = async (params = {}) => {
  const response = await api.post(API_ENDPOINTS.REPORTS.MARKET_ANALYSIS, params)
  return response.data
}

// Market Analysis dashboard — saare events ki exposure ek saath. Same shape as
// getMarketAnalysis (data[] = per market_id entry) but scoped to the dashboard.
export const getMarketAnalysisDashboard = async (params = {}) => {
  const response = await api.post(API_ENDPOINTS.REPORTS.MARKET_ANALYSIS_DASHBOARD, params)
  return response.data
}

// Credit Activity modal — Credit Deposit tab. Server needs { userId, amount,
// userType } + transactionPassword (verifyTransactionPassword middleware).
export const walletDeposit = async (payload) => {
  const response = await api.post(API_ENDPOINTS.WALLET.DEPOSIT_ADD, payload)
  return response.data
}

// Credit Withdraw tab — deposit ka ulta (child se paisa wapas).
export const walletWithdraw = async (payload) => {
  const response = await api.post(API_ENDPOINTS.WALLET.WITHDRAW, payload)
  return response.data
}

// Credit History tab — paginated ledger for one user ({ user_id, page, limit }).
export const getUserCreditHistory = async (payload) => {
  const response = await api.post(API_ENDPOINTS.WALLET.CREDIT_HISTORY, payload)
  return response.data
}

// Per-user book for one market: { eventid, game_type } -> { runners, data }
export const getUserBook = async (params) => {
  const response = await api.post('/admin/reports/user-book', params)
  return response.data
}

export const getTotalProfitLoss = async (params) => {
  const response = await api.post(API_ENDPOINTS.REPORTS.TOTAL_PROFIT_LOSS, params)
  return response.data
}

export const getMarketUserBook = async (matchId, marketType) => {
  try {
    const response = await api.get(`/d99/net-exposure/market-book/${matchId}`, {
      params: { marketType }
    })
    return response.data
  } catch (error) {
    return { success: false, message: error.message }
  }
}

export const declareResult = async (data) => {
  const response = await api.post(API_ENDPOINTS.INTERNALSETTLE.DECLARERESULT, data)
  return response.data
}

export const voidBet = async (data) => {
  const response = await api.post(API_ENDPOINTS.INTERNALSETTLE.VOID, data)
  return response.data
}

export const getOpenBetsForMarket = async (params) => {
  const response = await api.get(API_ENDPOINTS.INTERNALSETTLE.OPEN_BETS, { params })
  return response.data
}

export const voidSingleOpenBet = async (data) => {
  const response = await api.post(API_ENDPOINTS.INTERNALSETTLE.VOID_SINGLE_BET, data)
  return response.data
}

export const getSettledMarkets = async (params) => {
  const response = await api.get(API_ENDPOINTS.INTERNALSETTLE.SETTLED_MARKETS, { params })
  return response.data
}

export const getSettledBets = async (params) => {
  const response = await api.get(API_ENDPOINTS.INTERNALSETTLE.SETTLED_BETS, { params })
  return response.data
}

export const voidMarketAfterSettlement = async (data) => {
  const response = await api.post(API_ENDPOINTS.INTERNALSETTLE.VOID_MARKET_AFTER_SETTLEMENT, data)
  return response.data
}

export const voidBetAfterSettlement = async (data) => {
  const response = await api.post(API_ENDPOINTS.INTERNALSETTLE.VOID_BET_AFTER_SETTLEMENT, data)
  return response.data
}

// Casino void (owner-only). List = stuck rounds; void = { bet_ids, transactionPassword }.
export const getCasinoOpenBets = async () => {
  const response = await api.get(API_ENDPOINTS.INTERNALSETTLE.CASINO_OPEN_BETS)
  return response.data
}

export const voidCasinoBets = async (data) => {
  const response = await api.post(API_ENDPOINTS.INTERNALSETTLE.VOID_CASINO_BETS, data)
  return response.data
}

// Settle ho chuki casino bets + unka void (cash + inr dono badalte hain)
export const getCasinoSettledBets = async (params) => {
  const response = await api.get(API_ENDPOINTS.INTERNALSETTLE.CASINO_SETTLED_BETS, { params })
  return response.data
}

export const voidSettledCasinoBet = async (data) => {
  const response = await api.post(API_ENDPOINTS.INTERNALSETTLE.VOID_SETTLED_CASINO_BET, data)
  return response.data
}

export const getUserExposuresAdmin = async (userId) => {
  const response = await api.get(`/admin/user/exposures/${userId}`)
  return response.data
}

export default api
